import React, { useState, useEffect, useCallback } from 'react';
import { Monitor, Cpu, Watch, Plus, Search, Info, X, Wrench, ShoppingCart, Package, Clock, ChevronRight, Shield, Wifi, WifiOff, AlertTriangle, CheckCircle2, Ticket, MapPin, CreditCard, Truck, Tag, History, ExternalLink, ChevronLeft, Trash2, Edit3, ArrowRight, RefreshCw, Building2, Filter } from '../DeskeyIcons';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token');
  const res = await fetch(`${API}/api${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...opts.headers } });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Request failed'); }
  return res.json();
};
const fmt = (v) => `₹${(v || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '--';
const MODEL_COLORS = { pro: { bg: '#dbeafe', text: '#1d4ed8', border: '#bfdbfe', label: 'PRO' }, mini: { bg: '#d1fae5', text: '#047857', border: '#a7f3d0', label: 'MINI' }, wearable: { bg: '#fae8ff', text: '#9333ea', border: '#e9d5ff', label: 'WEARABLE' } };
const STATUS_STYLES = { open: 'bg-amber-50 text-amber-600 border-amber-100', in_progress: 'bg-blue-50 text-blue-600 border-blue-100', resolved: 'bg-emerald-50 text-emerald-600 border-emerald-100', closed: 'bg-gray-50 text-gray-600 border-gray-100' };
const ORDER_STATUS = { pending: 'bg-amber-50 text-amber-600 border-amber-100', confirmed: 'bg-blue-50 text-blue-600 border-blue-100', shipped: 'bg-violet-50 text-violet-600 border-violet-100', delivered: 'bg-emerald-50 text-emerald-600 border-emerald-100' };

const TABS = [
  { id: 'devices', label: 'My Devices', icon: Monitor },
  { id: 'shop', label: 'Shop', icon: ShoppingCart },
  { id: 'orders', label: 'Orders', icon: Package },
  { id: 'service', label: 'Service', icon: Wrench },
];

/* ── Model Badge ── */
function ModelBadge({ model }) {
  const c = MODEL_COLORS[model] || MODEL_COLORS.pro;
  return <span className="text-[10px] px-2 py-0.5 rounded-full font-bold border" style={{ background: c.bg, color: c.text, borderColor: c.border }}>{c.label}</span>;
}

/* ── Device Card ── */
function DeviceCard({ device, accent, onSelect, onDelete }) {
  const mc = MODEL_COLORS[device.model] || MODEL_COLORS.pro;
  const isOnline = device.last_online && (Date.now() - new Date(device.last_online).getTime()) < 2 * 60 * 60 * 1000;
  const warrantyLeft = device.warranty_end ? Math.ceil((new Date(device.warranty_end) - Date.now()) / (1000 * 60 * 60 * 24)) : 0;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow hover:shadow-md transition-all duration-300 group cursor-pointer overflow-hidden" onClick={() => onSelect(device)} data-testid={`device-card-${device.id}`}>
      <div className="h-2 w-full" style={{ background: mc.text }} />
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: mc.bg }}>
              {device.model === 'wearable' ? <Watch className="w-5 h-5" style={{ color: mc.text }} /> : device.model === 'pro' ? <Monitor className="w-5 h-5" style={{ color: mc.text }} /> : <Cpu className="w-5 h-5" style={{ color: mc.text }} />}
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">{device.nickname || 'Unnamed Device'}</p>
              <p className="text-[11px] text-gray-500 font-mono">{device.serial_number}</p>
            </div>
          </div>
          <ModelBadge model={device.model} />
        </div>
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2 text-xs">
            {isOnline ? <><Wifi className="w-3.5 h-3.5 text-emerald-500" /><span className="text-emerald-600 font-medium">Online</span></> : <><WifiOff className="w-3.5 h-3.5 text-gray-300" /><span className="text-gray-500">Last seen {fmtDateTime(device.last_online)}</span></>}
          </div>
          {device.current_teacher_email && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ background: accent }}>{(device.current_teacher_name || device.current_teacher_email)[0].toUpperCase()}</div>
              <span className="truncate">{device.current_teacher_name || device.current_teacher_email}</span>
            </div>
          )}
          {!device.current_teacher_email && <p className="text-xs text-gray-300 italic">No teacher logged in</p>}
          {device.department_name && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Building2 className="w-3.5 h-3.5 text-gray-300" />
              <span className="truncate">{device.department_name}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <Shield className="w-3 h-3 text-gray-300" />
            {warrantyLeft > 0 ? <span className={`text-[11px] font-medium ${warrantyLeft < 30 ? 'text-amber-500' : 'text-gray-500'}`}>{warrantyLeft}d warranty left</span> : <span className="text-[11px] text-red-400 font-medium">Warranty expired</span>}
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 transition-colors" />
        </div>
      </div>
    </div>
  );
}

/* ── Device Detail Modal ── */
function DeviceDetailModal({ device, accent, onClose, onDelete, departments, onAssignDept }) {
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const mc = MODEL_COLORS[device.model] || MODEL_COLORS.pro;
  const warrantyLeft = device.warranty_end ? Math.ceil((new Date(device.warranty_end) - Date.now()) / (1000 * 60 * 60 * 24)) : 0;

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await api(`/institution/devices/${device.id}/login-history`);
      setHistory(res.history || []);
    } catch { toast.error('Failed to load history'); }
    setLoading(false);
    setShowHistory(true);
  };

  const handleAssignDept = async (deptId) => {
    setAssigning(true);
    try {
      await onAssignDept(device.id, deptId);
    } catch (e) { toast.error(e.message); }
    setAssigning(false);
  };

  const Row = ({ icon: Icon, label, value, valueColor }) => (
    <div className="flex items-center gap-3 py-2.5">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${mc.text}10` }}>
        <Icon className="w-3.5 h-3.5" style={{ color: mc.text }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{label}</p>
        <p className={`text-sm font-medium truncate ${valueColor || 'text-gray-900'}`}>{value || '--'}</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="device-detail-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="h-20 relative" style={{ background: `linear-gradient(135deg, ${mc.text}, ${mc.text}cc)` }}>
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"><X className="w-4 h-4 text-white" /></button>
          <div className="absolute bottom-3 left-5 flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              {device.model === 'wearable' ? <Watch className="w-6 h-6 text-white" /> : device.model === 'pro' ? <Monitor className="w-6 h-6 text-white" /> : <Cpu className="w-6 h-6 text-white" />}
            </div>
            <div>
              <p className="text-white font-bold text-lg">{device.nickname}</p>
              <p className="text-white/70 text-xs font-mono">{device.serial_number}</p>
            </div>
          </div>
        </div>
        <div className="px-5 pt-4 pb-5 space-y-0 divide-y divide-slate-50">
          <Row icon={Tag} label="Model" value={device.model?.toUpperCase()} />
          <Row icon={Cpu} label="Firmware" value={device.firmware_version || '--'} />
          <Row icon={CheckCircle2} label="Status" value={device.status?.toUpperCase()} valueColor={device.status === 'active' ? 'text-emerald-600' : 'text-red-500'} />
          <Row icon={Clock} label="Registered" value={fmtDate(device.registered_at)} />
          <Row icon={Shield} label="Warranty Until" value={warrantyLeft > 0 ? `${fmtDate(device.warranty_end)} (${warrantyLeft} days left)` : 'Expired'} valueColor={warrantyLeft > 0 ? (warrantyLeft < 30 ? 'text-amber-500' : 'text-gray-900') : 'text-red-500'} />
          <Row icon={Wifi} label="Last Online" value={fmtDateTime(device.last_online)} />
          {device.current_teacher_email && <Row icon={Info} label="Current Teacher" value={`${device.current_teacher_name || ''} (${device.current_teacher_email})`} />}
        </div>

        {/* Department Assignment */}
        <div className="px-5 pb-3">
          <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-gray-50/50">
            <div className="flex items-center gap-2.5">
              <Building2 className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Department</p>
                <p className="text-sm font-medium text-gray-800">{device.department_name || 'Unassigned'}</p>
              </div>
            </div>
            <select
              value={device.department_id || ''}
              onChange={e => handleAssignDept(e.target.value)}
              disabled={assigning}
              className="h-8 text-xs rounded-lg border border-gray-200 px-2 bg-white text-gray-700 font-medium min-w-[130px]"
              data-testid="assign-department-select"
            >
              <option value="">No Department</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>

        {/* Login History Toggle */}
        <div className="px-5 pb-4">
          <button onClick={loadHistory} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors" data-testid="view-login-history">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Teacher Login History</span>
            </div>
            {loading ? <RefreshCw className="w-4 h-4 text-gray-300 animate-spin" /> : <ChevronRight className={`w-4 h-4 text-gray-300 transition-transform ${showHistory ? 'rotate-90' : ''}`} />}
          </button>
          {showHistory && (
            <div className="mt-2 border border-gray-100 rounded-xl overflow-hidden">
              {history.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-6">No login records yet</p>
              ) : (
                <div className="divide-y divide-slate-50 max-h-48 overflow-y-auto">
                  {history.map((h, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/50">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0" style={{ background: accent }}>{(h.teacher_name || h.teacher_email || '?')[0].toUpperCase()}</div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{h.teacher_name || h.teacher_email}</p>
                          <p className="text-[10px] text-gray-500">{h.teacher_email}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="text-[11px] text-gray-600">{fmtDateTime(h.logged_in_at)}</p>
                        {h.logged_out_at && <p className="text-[10px] text-gray-300">out: {fmtDateTime(h.logged_out_at)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 pb-5 flex gap-2">
          <button onClick={() => { onDelete(device.id); onClose(); }} className="flex-1 h-10 rounded-xl text-xs font-semibold text-red-500 border border-red-200 bg-red-50 hover:bg-red-100 transition-colors flex items-center justify-center gap-1.5" data-testid="unregister-device">
            <Trash2 className="w-3.5 h-3.5" />Unregister
          </button>
          <button onClick={onClose} className="flex-1 h-10 rounded-xl text-xs font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
}

/* Register Device modal removed */

/* ── Service Ticket Modal ── */
function CreateTicketModal({ devices, accent, onCreate, onClose }) {
  const [deviceId, setDeviceId] = useState(devices[0]?.id || '');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) { toast.error('Please fill in all fields'); return; }
    setLoading(true);
    try {
      await onCreate({ device_id: deviceId, subject, description, priority });
      onClose();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-ticket-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-1">Raise Service Ticket</h3>
          <p className="text-xs text-gray-500 mb-5">Report an issue with your device for warranty service or repair.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Device</label>
              <select value={deviceId} onChange={e => setDeviceId(e.target.value)} className="w-full h-10 mt-1 px-3 text-sm rounded-lg border border-gray-200 bg-white" data-testid="ticket-device-select">
                {devices.map(d => <option key={d.id} value={d.id}>{d.nickname} ({d.serial_number})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description of the issue" className="w-full h-10 mt-1 px-3 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent bg-white" style={{ '--tw-ring-color': `${accent}40` }} data-testid="ticket-subject" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Description</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder="Describe the issue in detail..." className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent bg-white resize-none" style={{ '--tw-ring-color': `${accent}40` }} data-testid="ticket-description" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Priority</label>
              <div className="flex gap-2 mt-1">
                {['low', 'medium', 'high'].map(p => (
                  <button key={p} onClick={() => setPriority(p)} className={`flex-1 h-9 rounded-lg text-xs font-semibold border transition-colors ${priority === p ? 'text-white border-transparent' : 'text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                    style={priority === p ? { background: p === 'high' ? '#ef4444' : p === 'medium' ? '#f59e0b' : '#6b7280' } : {}} data-testid={`priority-${p}`}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 h-10 rounded-xl text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50">Cancel</button>
          <Button onClick={handleSubmit} disabled={loading} className="flex-1 h-10 rounded-xl text-sm font-semibold text-white" style={{ background: accent }} data-testid="submit-ticket">
            {loading ? 'Submitting...' : 'Submit Ticket'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Order Modal ── */
function OrderModal({ cart, products, accent, onPlace, onClose }) {
  const [address, setAddress] = useState({ line1: '', line2: '', city: '', state: '', pin: '' });
  const [billingAddress, setBillingAddress] = useState({ line1: '', line2: '', city: '', state: '', pin: '' });
  const [sameAddress, setSameAddress] = useState(true);
  const [gstin, setGstin] = useState('');
  const [loading, setLoading] = useState(false);
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const gst = Math.round(subtotal * 0.18);
  const total = subtotal + gst;
  const handleSubmit = async () => {
    if (!address.line1 || !address.city || !address.state || !address.pin) { toast.error('Please fill delivery address'); return; }
    setLoading(true);
    try {
      await onPlace({
        items: cart.map(i => ({ product_id: i.id, quantity: i.quantity, color: i.color || null })),
        delivery_address: address,
        billing_address: sameAddress ? address : billingAddress,
        gstin: gstin || null,
      });
      onClose();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  const AddrFields = ({ addr, setAddr, prefix }) => (
    <div className="space-y-2">
      <input value={addr.line1} onChange={e => setAddr(a => ({ ...a, line1: e.target.value }))} placeholder="Address Line 1" className="w-full h-9 px-3 text-sm rounded-lg border border-gray-200 bg-white" data-testid={`${prefix}-line1`} />
      <input value={addr.line2} onChange={e => setAddr(a => ({ ...a, line2: e.target.value }))} placeholder="Address Line 2 (optional)" className="w-full h-9 px-3 text-sm rounded-lg border border-gray-200 bg-white" data-testid={`${prefix}-line2`} />
      <div className="grid grid-cols-3 gap-2">
        <input value={addr.city} onChange={e => setAddr(a => ({ ...a, city: e.target.value }))} placeholder="City" className="h-9 px-3 text-sm rounded-lg border border-gray-200 bg-white" data-testid={`${prefix}-city`} />
        <input value={addr.state} onChange={e => setAddr(a => ({ ...a, state: e.target.value }))} placeholder="State" className="h-9 px-3 text-sm rounded-lg border border-gray-200 bg-white" data-testid={`${prefix}-state`} />
        <input value={addr.pin} onChange={e => setAddr(a => ({ ...a, pin: e.target.value }))} placeholder="PIN" className="h-9 px-3 text-sm rounded-lg border border-gray-200 bg-white" data-testid={`${prefix}-pin`} />
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="order-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div><h3 className="text-base font-bold text-gray-900">Complete Your Order</h3><p className="text-xs text-gray-500 mt-0.5">{cart.length} item(s) in cart</p></div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"><X className="w-4 h-4 text-gray-500" /></button>
        </div>
        <div className="px-6 py-4 space-y-5">
          {/* Order Items */}
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-2">Items</p>
            <div className="space-y-2">
              {cart.map(item => (
                <div key={`${item.id}-${item.color || ''}`} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-100">
                  <div><p className="text-sm font-medium text-gray-800">{item.name}{item.color ? ` — ${item.color}` : ''}</p><p className="text-[11px] text-gray-500">Qty: {item.quantity}</p></div>
                  <p className="text-sm font-bold text-gray-900 tabular-nums">{fmt(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Delivery Address */}
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Delivery Address</p>
            <AddrFields addr={address} setAddr={setAddress} prefix="delivery" />
          </div>
          {/* Billing Address */}
          <div>
            <label className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked={sameAddress} onChange={e => setSameAddress(e.target.checked)} className="rounded" data-testid="same-address-check" />
              <span className="text-xs text-gray-600">Billing address same as delivery</span>
            </label>
            {!sameAddress && <AddrFields addr={billingAddress} setAddr={setBillingAddress} prefix="billing" />}
          </div>
          {/* GSTIN */}
          <div>
            <label className="text-xs font-semibold text-gray-700">GSTIN <span className="text-gray-300 font-normal">(optional)</span></label>
            <input value={gstin} onChange={e => setGstin(e.target.value)} placeholder="e.g. 29AABCU9603R1ZM" className="w-full h-9 mt-1 px-3 text-sm rounded-lg border border-gray-200 bg-white" data-testid="order-gstin" />
          </div>
          {/* Price Summary */}
          <div className="bg-gray-50 rounded-xl border border-gray-100 p-4 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal</span><span className="font-medium text-gray-800 tabular-nums">{fmt(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-600">CGST (9%)</span><span className="text-gray-600 tabular-nums">{fmt(Math.round(gst / 2))}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-600">SGST (9%)</span><span className="text-gray-600 tabular-nums">{fmt(Math.round(gst / 2))}</span></div>
            <div className="flex justify-between text-sm pt-2 border-t border-gray-200"><span className="font-bold text-gray-900">Total</span><span className="font-extrabold text-gray-900 tabular-nums">{fmt(total)}</span></div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-3 flex gap-3">
          <button onClick={onClose} className="flex-1 h-10 rounded-xl text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50">Cancel</button>
          <Button onClick={handleSubmit} disabled={loading} className="flex-1 h-10 rounded-xl text-sm font-bold text-white" style={{ background: accent }} data-testid="place-order-btn">
            {loading ? 'Placing...' : `Pay ${fmt(total)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Product Card ── */
function ProductCard({ product, accent, onAdd }) {
  const colors = product.colors || [];
  const [selectedColor, setSelectedColor] = useState(colors[0] || null);
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden group hover:shadow-md transition-all" data-testid={`product-${product.id}`}>
      <div className="h-36 flex items-center justify-center relative bg-gray-50">
        {product.image ? <img src={product.image} alt={product.name} className="w-full h-full object-cover" /> : <Monitor className="w-12 h-12 text-gray-300" />}
      </div>
      <div className="p-4">
        <h4 className="text-sm font-bold text-gray-900 mb-1">{product.name}</h4>
        {product.description && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{product.description}</p>}
        {colors.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide mb-1.5">Colour</p>
            <div className="flex flex-wrap gap-1.5">
              {colors.map(c => (
                <button key={c} type="button" onClick={() => setSelectedColor(c)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${selectedColor === c ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                  style={selectedColor === c ? { background: accent } : {}}
                  data-testid={`color-${product.id}-${c}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div>
            <p className="text-lg font-extrabold text-gray-900 tabular-nums">{fmt(product.price)}</p>
            <p className="text-[10px] text-gray-500">+ 18% GST</p>
          </div>
          <Button onClick={() => onAdd(product, selectedColor)} className="h-9 px-4 rounded-lg text-xs font-bold text-white gap-1" style={{ background: accent }} data-testid={`add-to-cart-${product.id}`}>
            <ShoppingCart className="w-3.5 h-3.5" />Add
          </Button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════ */
/* ███  MAIN COMPONENT  ███ */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function DeviceManagementSection({ accent }) {
  const A = accent || '#34A853';
  const [tab, setTab] = useState('devices');
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [search, setSearch] = useState('');
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [devRes, statRes, tickRes, ordRes, prodRes, deptRes] = await Promise.all([
        api('/institution/devices'),
        api('/institution/devices/stats'),
        api('/institution/devices/service-tickets'),
        api('/institution/devices/orders'),
        api('/institution/devices/products'),
        api('/institution/departments'),
      ]);
      setDevices(devRes.devices || []);
      setStats(statRes);
      setTickets(tickRes.tickets || []);
      setOrders(ordRes.orders || []);
      setProducts(prodRes.products || []);
      setDepartments(deptRes.departments || []);
    } catch (e) { toast.error('Failed to load device data'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Demo seeding removed — devices are provisioned via Sales Ops allocation

  // Register via pairing flow removed from UI

  const handleDelete = async (deviceId) => {
    try {
      await api(`/institution/devices/${deviceId}`, { method: 'DELETE' });
      toast.success('Device unregistered');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleAssignDept = async (deviceId, deptId) => {
    try {
      const updated = await api(`/institution/devices/${deviceId}`, { method: 'PUT', body: JSON.stringify({ department_id: deptId }) });
      toast.success(deptId ? `Assigned to ${updated.department_name}` : 'Removed from department');
      setSelectedDevice(updated);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleCreateTicket = async (data) => {
    await api('/institution/devices/service-tickets', { method: 'POST', body: JSON.stringify(data) });
    toast.success('Service ticket created');
    load();
  };

  const addToCart = (product, color) => {
    setCart(c => {
      const existing = c.find(i => i.id === product.id && i.color === color);
      if (existing) return c.map(i => (i.id === product.id && i.color === color) ? { ...i, quantity: i.quantity + 1 } : i);
      return [...c, { id: product.id, name: product.name, price: product.price, quantity: 1, color: color || null }];
    });
    toast.success(`${product.name} added to cart`);
  };

  const handlePlaceOrder = async (data) => {
    const checkout = await api('/institution/devices/orders/checkout', { method: 'POST', body: JSON.stringify(data) });
    const user = JSON.parse(localStorage.getItem('desky_user') || '{}');
    const options = {
      key: checkout.key_id,
      amount: checkout.amount,
      currency: checkout.currency,
      name: 'Deskey Shop Hall',
      description: 'Device order',
      order_id: checkout.razorpay_order_id,
      handler: async (response) => {
        try {
          await api('/institution/devices/orders/verify-payment', {
            method: 'POST',
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          toast.success('Payment successful! Order placed — check the Orders tab.');
          setCart([]);
          load();
        } catch (e) { toast.error(e.message || 'Payment verification failed'); }
      },
      prefill: { name: user.full_name || '', email: user.email || '' },
      theme: { color: A },
      modal: { ondismiss: () => toast.info('Payment cancelled') },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const filteredDevices = devices.filter(d => {
    if (deptFilter !== 'all') {
      if (deptFilter === 'unassigned') { if (d.department_id) return false; }
      else { if (d.department_id !== deptFilter) return false; }
    }
    if (!search) return true;
    const s = search.toLowerCase();
    return (d.nickname || '').toLowerCase().includes(s) || (d.serial_number || '').toLowerCase().includes(s) || (d.current_teacher_email || '').toLowerCase().includes(s) || (d.department_name || '').toLowerCase().includes(s);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]" data-testid="devices-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-slate-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading devices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-120px)]" data-testid="device-management-section">
      {selectedDevice && <DeviceDetailModal device={selectedDevice} accent={A} onClose={() => setSelectedDevice(null)} onDelete={handleDelete} departments={departments} onAssignDept={handleAssignDept} />}
      {showCreateTicket && devices.length > 0 && <CreateTicketModal devices={devices} accent={A} onCreate={handleCreateTicket} onClose={() => setShowCreateTicket(false)} />}
      {showOrder && cart.length > 0 && <OrderModal cart={cart} products={products} accent={A} onPlace={handlePlaceOrder} onClose={() => setShowOrder(false)} />}

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0" data-testid="device-stat-cards">
        <div className="bg-white border border-gray-200 rounded-xl shadow p-4 hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center"><Monitor className="w-4 h-4 text-blue-500" /></div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total Devices</span>
          </div>
          <p className="text-xl font-extrabold text-gray-900" data-testid="stat-total-devices">{stats?.total || 0}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">{stats?.pro || 0} Pro + {stats?.mini || 0} Mini{stats?.wearable ? ` + ${stats.wearable} Wearable` : ''}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow p-4 hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="w-4 h-4 text-emerald-500" /></div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Active</span>
          </div>
          <p className="text-xl font-extrabold text-gray-900" data-testid="stat-active-devices">{stats?.active || 0}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Currently registered</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow p-4 hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center"><Ticket className="w-4 h-4 text-amber-500" /></div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Open Tickets</span>
          </div>
          <p className="text-xl font-extrabold text-gray-900" data-testid="stat-open-tickets">{stats?.open_tickets || 0}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Service requests</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow p-4 hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center"><Truck className="w-4 h-4 text-violet-500" /></div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Orders</span>
          </div>
          <p className="text-xl font-extrabold text-gray-900" data-testid="stat-pending-orders">{stats?.pending_orders || 0}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">In progress</p>
        </div>
      </div>

      {/* Tabs + Actions */}
      <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all ${tab === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`} data-testid={`dev-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" strokeWidth={tab === t.id ? 2 : 1.5} />
              {t.label}
              {t.id === 'shop' && cart.length > 0 && <span className="ml-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">{cart.reduce((s, i) => s + i.quantity, 0)}</span>}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          {tab === 'devices' && null}
          {tab === 'service' && devices.length > 0 && (
            <Button onClick={() => setShowCreateTicket(true)} className="h-9 px-3.5 text-xs font-bold rounded-lg text-white gap-1.5" style={{ background: A }} data-testid="create-ticket-btn"><Plus className="w-3.5 h-3.5" />Raise Ticket</Button>
          )}
          {tab === 'shop' && cart.length > 0 && (
            <Button onClick={() => setShowOrder(true)} className="h-9 px-3.5 text-xs font-bold rounded-lg text-white gap-1.5" style={{ background: A }} data-testid="checkout-btn"><CreditCard className="w-3.5 h-3.5" />Checkout ({cart.reduce((s, i) => s + i.quantity, 0)})</Button>
          )}
        </div>
      </div>

      {/* ─── DEVICES TAB ─── */}
      {tab === 'devices' && (
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="devices-grid-container">
          {devices.length > 0 && (
            <div className="mb-3 flex items-center gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search devices..." className="w-full h-9 pl-9 pr-3 text-sm rounded-lg border border-gray-200 bg-white placeholder:text-gray-300" data-testid="device-search" />
              </div>
              {departments.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-gray-500" />
                  <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="h-9 text-xs rounded-lg border border-gray-200 px-2.5 bg-white text-gray-700 font-medium" data-testid="dept-filter">
                    <option value="all">All Departments</option>
                    <option value="unassigned">Unassigned</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
          {filteredDevices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4"><Monitor className="w-8 h-8 text-gray-300" /></div>
              <p className="text-base font-semibold text-gray-600 mb-1">{devices.length === 0 ? 'No devices registered yet' : 'No matching devices'}</p>
              <p className="text-xs text-gray-500 max-w-xs">
                {devices.length === 0 ? 'Devices appear here when allocated by your Sales Ops team, or register using the pairing code from the device.' : 'Try a different search term.'}
              </p>
              {devices.length === 0 && null}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredDevices.map(d => <DeviceCard key={d.id} device={d} accent={A} onSelect={setSelectedDevice} onDelete={handleDelete} />)}
            </div>
          )}
        </div>
      )}

      {/* ─── SHOP TAB ─── */}
      {tab === 'shop' && (
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="shop-content">
          {products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <Package className="w-12 h-12 text-gray-200 mb-3" />
              <p className="text-sm font-semibold text-gray-600 mb-1">Shop is empty</p>
              <p className="text-xs text-gray-500">Your Deskey account manager hasn't listed any products yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {products.map(p => <ProductCard key={p.id} product={p} accent={A} onAdd={addToCart} />)}
            </div>
          )}
        </div>
      )}

      {/* ─── ORDERS TAB ─── */}
      {tab === 'orders' && (
        <div className="bg-white border border-gray-200 rounded-xl shadow flex-1 min-h-0 overflow-y-auto" data-testid="orders-content">
          {orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <Package className="w-12 h-12 text-gray-200 mb-3" />
              <p className="text-sm font-semibold text-gray-600 mb-1">No orders yet</p>
              <p className="text-xs text-gray-500">Browse the shop and place your first order.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {orders.map(order => (
                <div key={order.id} className="p-5 hover:bg-gray-50/50 transition-colors" data-testid={`order-${order.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs text-gray-500 font-mono mb-0.5">#{order.id.slice(0, 8).toUpperCase()}</p>
                      <p className="text-sm font-bold text-gray-900">{order.items?.length} item(s) — {fmt(order.total)}</p>
                    </div>
                    <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold border ${ORDER_STATUS[order.status] || ORDER_STATUS.pending}`}>{order.status?.toUpperCase()}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {order.items?.map((item, i) => (
                      <span key={i} className="text-[11px] bg-gray-100 text-gray-700 px-2 py-1 rounded-lg">{item.name}{item.color ? ` (${item.color})` : ''} x{item.quantity}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Placed: {fmtDate(order.created_at)}</span>
                    {order.estimated_delivery && <span>Est. Delivery: {fmtDate(order.estimated_delivery)}</span>}
                    {order.tracking_link && <a href={order.tracking_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-500 hover:underline"><ExternalLink className="w-3 h-3" />Track</a>}
                    <span className="ml-auto">Subtotal: {fmt(order.subtotal)} + GST: {fmt(order.gst_amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── SERVICE TAB ─── */}
      {tab === 'service' && (
        <div className="bg-white border border-gray-200 rounded-xl shadow flex-1 min-h-0 overflow-y-auto" data-testid="service-content">
          {tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <Wrench className="w-12 h-12 text-gray-200 mb-3" />
              <p className="text-sm font-semibold text-gray-600 mb-1">No service tickets</p>
              <p className="text-xs text-gray-500 max-w-xs">Need device service or warranty claim? Raise a ticket and our team will assist.</p>
              {devices.length > 0 && (
                <Button onClick={() => setShowCreateTicket(true)} className="mt-4 h-9 px-4 text-xs font-bold rounded-lg text-white gap-1.5" style={{ background: A }}><Plus className="w-3.5 h-3.5" />Raise Ticket</Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {tickets.map(ticket => (
                <div key={ticket.id} className="p-5 hover:bg-gray-50/50 transition-colors" data-testid={`ticket-${ticket.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{ticket.subject}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{ticket.device_nickname} ({ticket.device_serial})</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${ticket.priority === 'high' ? 'bg-red-50 text-red-500 border-red-100' : ticket.priority === 'medium' ? 'bg-amber-50 text-amber-500 border-amber-100' : 'bg-gray-50 text-gray-500 border-gray-100'}`}>{ticket.priority?.toUpperCase()}</span>
                      <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold border ${STATUS_STYLES[ticket.status] || STATUS_STYLES.open}`}>{ticket.status?.replace('_', ' ').toUpperCase()}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2 mb-2">{ticket.description}</p>
                  <div className="flex items-center gap-3 text-[11px] text-gray-500">
                    <span>Created: {fmtDate(ticket.created_at)}</span>
                    {ticket.resolved_at && <span>Resolved: {fmtDate(ticket.resolved_at)}</span>}
                    {ticket.resolution_notes && <span className="text-emerald-500">Resolution: {ticket.resolution_notes}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
